/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      const handleInput = debounce(this.handleInput.bind(this));
      this.input.addEventListener('input', () => {
        // Invalidate immediately, before the debounce: old responses must never
        // replace suggestions for the text the customer is currently typing.
        this.close();
        this.removeAttribute('results');
        handleInput();
      });
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
      this.results.addEventListener('click', (event) => {
        if (event.target.closest('[role="tab"]')) {
          // Tabs switch during bubbling; measure the panel actually shown afterward.
          queueMicrotask(() => this.trackNativeResults());
        }
      });
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
        return;
      }

      const originalQuery = this.getQuery();
      const normalizedQuery = globalThis.RicochetSearchNormalizer?.normalizeSearchQuery?.(originalQuery)
        || globalThis.RicochetSearchNormalizer?.normalizeQuery(originalQuery)
        || originalQuery;
      if (normalizedQuery !== originalQuery) {
        let originalQueryInput = this.querySelector('input[name="original_q"]');
        if (!originalQueryInput) {
          originalQueryInput = document.createElement('input');
          originalQueryInput.type = 'hidden';
          originalQueryInput.name = 'original_q';
          this.querySelector('.search').append(originalQueryInput);
        }
        originalQueryInput.value = originalQuery;
      } else {
        this.querySelector('input[name="original_q"]')?.remove();
      }
      this.input.value = normalizedQuery;
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      globalThis.RicochetDiscoveryAnalytics?.cancel(this);
      this.requestController?.abort();
      const controller = new AbortController();
      this.requestController = controller;
      this.setLiveRegionLoadingState();

      const collectionSearch = globalThis.RicochetCollectionSearch;
      if (collectionSearch && !this.productTypeInput?.value && !collectionSearch.isProductQuery(searchTerm)) {
        const matches = collectionSearch.rank(globalThis.RicochetSearchData?.collections || [], searchTerm);
        if (matches.length) {
          this.renderCollectionResults(matches);
          return;
        }
      }

      const normalizedQuery = globalThis.RicochetSearchNormalizer?.normalizeSearchQuery?.(searchTerm)
        || globalThis.RicochetSearchNormalizer?.normalizeQuery(searchTerm)
        || searchTerm;
      const normalizedSearchTerm = globalThis.RicochetSearchNormalizer?.normalizeProductQuery?.(normalizedQuery)
        || normalizedQuery;

      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let searchParams = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(normalizedSearchTerm)}`;
      } else {
        searchParams = `q=${encodeURIComponent(normalizedSearchTerm)}`;
      }

      searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${
        theme.settings.pSearchLimitScope
      }`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      searchParams += '&section_id=predictive-search';

      const queryKey = searchParams.replace(' ', '-').toLowerCase();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`, {
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        if (controller.signal.aborted || this.requestController !== controller || this.getQuery() !== searchTerm) return;
        this.prioritizeExactMatches(resultsEl, normalizedSearchTerm);
        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        if (Object.keys(this.cachedResults).length >= 50) delete this.cachedResults[Object.keys(this.cachedResults)[0]];
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup);
      } catch (error) {
        if (controller.signal.aborted || this.requestController !== controller) return;
        globalThis.RicochetDiscoveryAnalytics?.emit('search_failed', {
          surface: 'predictive', query: searchTerm, placement: this.input.id === 'header-search' ? 'header' : 'page'
        });
        this.removeAttribute('results');
        this.close();
      }
    }

    renderCollectionResults(matches) {
      const wrapper = document.createElement('div');
      wrapper.className = 'predictive-search absolute bg-theme-bg text-theme-text text-start';
      const scroller = document.createElement('div');
      scroller.className = 'predictive-search__scroller overflow-auto';
      const panel = document.createElement('div');
      panel.className = 'predictive-search__tabpanel';
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-label', 'Matching collections');
      const heading = document.createElement('h3');
      heading.className = 'predictive-result__title h6 font-body font-bold';
      heading.textContent = 'Machines / collections';
      const list = document.createElement('ul');
      list.className = 'predictive-search__list';
      list.setAttribute('role', 'list');
      matches.slice(0, Math.max(1, Math.min(10, Number(theme.settings.pSearchLimit) || 5))).forEach(({ document: collection }, index) => {
        const item = document.createElement('li');
        item.className = 'predictive-search__item';
        item.id = `${this.input.id}-collection-${index}`;
        item.setAttribute('role', 'option');
        const link = document.createElement('a');
        link.className = 'predictive-result block focus-inset js-search-link';
        link.href = `/collections/${encodeURIComponent(collection.h)}`;
        link.textContent = collection.t;
        item.append(link);
        list.append(item);
      });
      const submitItem = document.createElement('li');
      submitItem.className = 'predictive-search__item js-submit';
      submitItem.id = `${this.input.id}-collection-all`;
      submitItem.setAttribute('role', 'option');
      const submit = document.createElement('button');
      submit.className = 'predictive-search__view-all link w-full text-start js-search-link';
      submit.type = 'submit';
      submit.textContent = `View all ${matches.length} matching collections →`;
      submitItem.append(submit);
      list.append(submitItem);
      panel.append(heading, list);
      scroller.append(panel);
      wrapper.append(scroller);
      this.renderResults(wrapper.outerHTML);
      globalThis.RicochetDiscoveryAnalytics?.results('predictive', this, {
        query: this.getQuery(), mode: 'collection', total: matches.length,
        shown: list.querySelectorAll('li:not(.js-submit)').length
      });
      this.setLiveRegionText(`${matches.length} matching collections`);
    }

    /** Keep exact machines/SKUs ahead of Shopify's broader prefix matches. */
    prioritizeExactMatches(element, query) {
      const compact = (value) => String(value || '').normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
      const key = compact(query);
      const score = (item) => {
        const { searchMake: make, searchModel: model, searchSubmodel: submodel, searchVehicle: vehicle, searchSkus } = item.dataset;
        if (searchSkus) {
          try {
            if (JSON.parse(searchSkus).some((sku) => compact(sku) === key)) return 2;
          } catch { /* Missing/invalid optional data leaves Shopify's order intact. */ }
        }
        const fullModel = submodel && !compact(model).includes(compact(submodel))
          ? [model, submodel].filter(Boolean).join(' ') : model;
        return [fullModel, [make, fullModel].filter(Boolean).join(' '), vehicle]
          .some((value) => value && compact(value) === key) ? 1 : 0;
      };
      element.querySelectorAll('.predictive-search__list').forEach((list) => {
        const items = [...list.children].filter((item) => item.matches('.predictive-search__item:not(.js-submit)'));
        const submit = list.querySelector('.js-submit');
        items.sort((left, right) => score(right) - score(left))
          .forEach((item) => list.insertBefore(item, submit));
      });
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      const query = this.getQuery();
      const withoutYear = globalThis.RicochetSearchNormalizer?.stripModelYears?.(query) || query;
      if (withoutYear !== query) {
        const note = document.createElement('p');
        note.className = 'predictive-result__sub-title text-sm';
        note.textContent = 'Year not filtered—check fitment years before ordering.';
        this.results.querySelector('.predictive-search__scroller')?.prepend(note);
      }
      if (globalThis.RicochetCollectionSearch?.isProductQuery(this.getQuery())) {
        const productTab = [...this.results.querySelectorAll('[role="tab"]')]
          .find((tab) => tab.getAttribute('aria-controls') === `${this.input.id}-products`);
        productTab?.click();
      }
      this.setAttribute('results', true);
      this.open();
      this.trackNativeResults();
    }

    trackNativeResults() {
      if (!this.hasAttribute('open')) return;
      const panel = this.results.querySelector('[role="tabpanel"]:not([hidden])');
      const shown = panel?.querySelectorAll('.predictive-search__item:not(.js-submit)').length || 0;
      globalThis.RicochetDiscoveryAnalytics?.results('predictive', this, {
        query: this.getQuery(), mode: panel?.id.endsWith('-products') ? 'product' : 'native', total: shown, shown
      });
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      globalThis.RicochetDiscoveryAnalytics?.cancel(this);
      this.requestController?.abort();
      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
